// components/admin/StockAdjustmentModal.tsx
import React, { useEffect, useState } from 'react';
import type { Product, ProductVariant, Warehouse } from '../../types';
import { stockAPI } from '../../api/stock';
import type { StockAdjustment, StockAdjustmentReason } from '../../types/stock';
import toast from 'react-hot-toast';

interface StockAdjustmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    product: Product;
    variants?: ProductVariant[];
    warehouses: Warehouse[];
}

export const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    product,
    variants,
    warehouses
}) => {
    const [loading, setLoading] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState<number | null>(
        variants && variants.length > 0 ? variants[0].id : null
    );
    const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
    const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove'>('add');
    const [quantity, setQuantity] = useState<number>(0);
    const [reason, setReason] = useState<StockAdjustmentReason>('restock');
    const [reasonText, setReasonText] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [bulkReason, setBulkReason] = useState<StockAdjustmentReason>('restock');
    const [bulkReasonText, setBulkReasonText] = useState('');
    const [bulkReferenceNumber, setBulkReferenceNumber] = useState('');
    const [bulkStockMode, setBulkStockMode] = useState(false);
    const [bulkAdjustmentType, setBulkAdjustmentType] = useState<'add' | 'remove'>('add');
    const [bulkStockAdjustments, setBulkStockAdjustments] = useState<Array<{
        variantId: number;
        variantName: string;
        quantity: number;
    }>>([]);
    const addReasons: { value: StockAdjustmentReason; label: string; requiresRef?: boolean }[] = [
        { value: 'restock', label: 'Restock', requiresRef: true },
        { value: 'customer_return', label: 'Customer Return', requiresRef: true },
        { value: 'inventory_correction', label: 'Inventory Correction' },
        { value: 'quality_control', label: 'Quality Control' },
        { value: 'sample', label: 'Sample/Tester' },
        { value: 'other', label: 'Other' }
    ];
    const removeReasons: { value: StockAdjustmentReason; label: string; requiresRef?: boolean }[] = [
        { value: 'damaged', label: 'Damaged Goods' },
        { value: 'lost', label: 'Lost/Missing' },
        { value: 'customer_return', label: 'Customer Return', requiresRef: true },
        { value: 'inventory_correction', label: 'Inventory Correction' },
        { value: 'quality_control', label: 'Quality Control' },
        { value: 'sample', label: 'Sample/Tester' },
        { value: 'other', label: 'Other' }
    ];

    // Get current reasons based on adjustment type
    const currentReasons = adjustmentType === 'add' ? addReasons : removeReasons;
    const bulkCurrentReasons = bulkAdjustmentType === 'add' ? addReasons : removeReasons;

    // Initialize when modal opens
    useEffect(() => {
        if (variants && variants.length > 0) {
            setBulkStockAdjustments(
                variants.map(v => ({
                    variantId: v.id,
                    variantName: v.name,
                    quantity: 0
                }))
            );
        }
    }, [variants]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        // Validation
        if (!selectedWarehouse) {
            toast.error('Please select a warehouse');
            setLoading(false);
            return;
        }

        try {
            if (bulkStockMode) {
                // BULK MODE
                const adjustmentsToProcess = bulkStockAdjustments.filter(adj => adj.quantity > 0);

                if (adjustmentsToProcess.length === 0) {
                    toast.error('Enter at least one quantity');
                    setLoading(false);
                    return;
                }

                // Create promises with correct bulk reason fields
                const adjustmentPromises = adjustmentsToProcess.map(adj =>
                    stockAPI.adjustStock({
                        product_id: product.id,
                        variant_id: adj.variantId,
                        warehouse_id: selectedWarehouse,
                        quantity_change: bulkAdjustmentType === 'add' ? adj.quantity : -adj.quantity,
                        reason: bulkReason,
                        reason_text: bulkReason === 'other' ? bulkReasonText : undefined,
                        notes,
                        reference_number: bulkReferenceNumber || undefined
                    })
                );

                // Wait for all and check results
                const results = await Promise.all(adjustmentPromises);

                // Check if any failed
                const failed = results.filter(r => r.error);
                if (failed.length > 0) {
                    throw new Error(`${failed.length} adjustment(s) failed`);
                }

                toast.success(`${adjustmentsToProcess.length} variant(s) updated successfully`);

            } else {
                // SINGLE MODE - Fix null/undefined issue
                let variantId: number | undefined;
                if (variants && variants.length === 1) {
                    variantId = variants[0].id;
                } else if (selectedVariant) {
                    variantId = selectedVariant;
                }

                const adjustment: StockAdjustment = {
                    product_id: product.id,
                    variant_id: variantId,  // Now properly typed as number | undefined
                    warehouse_id: selectedWarehouse,
                    quantity_change: adjustmentType === 'add' ? quantity : -quantity,
                    reason,
                    reason_text: reason === 'other' ? reasonText : undefined,
                    notes,
                    reference_number: referenceNumber || undefined
                };

                const response = await stockAPI.adjustStock(adjustment);
                if (response.error) {
                    throw new Error(response.error);
                }

                toast.success('Stock adjusted successfully');
            }

            // Only close on complete success
            onSuccess();
            onClose();

        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to adjust stock');
            console.error('Stock adjustment error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

                <div className="relative bg-white rounded-lg max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium">Adjust Stock</h3>
                        {variants && variants.length > 1 && (
                            <button
                                type="button"
                                onClick={() => setBulkStockMode(!bulkStockMode)}
                                className="text-sm text-blue-600 hover:text-blue-700"
                            >
                                {bulkStockMode ? 'Switch to single mode' : 'Switch to bulk mode'}
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Product Info */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="font-medium">{product.title}</p>
                            <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                        </div>

                        {bulkStockMode ? (
                            // BULK MODE - Adjust multiple variants at once
                            <>
                                {/* Individual variant inputs */}
                                <div className="border rounded-lg max-h-48 overflow-y-auto text-sm">
                                    {bulkStockAdjustments.map((item, index) => (
                                        <div key={item.variantId} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0">
                                            <span className="flex-1 truncate">{item.variantName}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const updated = [...bulkStockAdjustments];
                                                    updated[index].quantity = Number(e.target.value);
                                                    setBulkStockAdjustments(updated);
                                                }}
                                                className="w-20 px-2 py-1 border rounded text-sm"
                                            />
                                        </div>
                                    ))}
                                </div>


                                <div className="bg-blue-50 p-3 rounded-lg">
                                    <p className="text-sm text-blue-700">
                                        Set quantities for each variant. Leave as 0 to skip.
                                    </p>
                                </div>
                            </>
                        ) : (
                            // SINGLE MODE - Original form
                            <>
                                {/* Variant Selection (if multiple) */}
                                {variants && variants.length === 1 ? (
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-sm text-gray-600">
                                            Adjusting stock for: <span className="font-medium">{variants[0]?.name}</span>
                                        </p>
                                    </div>
                                ) : variants && variants.length > 1 ? (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Variant</label>
                                        <select
                                            value={selectedVariant ?? ''}
                                            onChange={(e) => setSelectedVariant(Number(e.target.value))}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        >
                                            {variants.map(v => (
                                                <option key={v.id} value={v.id}>{v.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : null}

                                {/* Adjustment Type & Quantity */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Type</label>
                                        <select
                                            value={adjustmentType}
                                            onChange={(e) => setAdjustmentType(e.target.value as 'add' | 'remove')}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        >
                                            <option value="add">Add Stock (+)</option>
                                            <option value="remove">Remove Stock (-)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Quantity</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={quantity}
                                            onChange={(e) => setQuantity(Number(e.target.value))}
                                            required
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {bulkStockMode && (
                            <>
                                {/* Bulk adjustment type selector */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Adjustment Type</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="bulkAdjustmentType"
                                                value="add"
                                                checked={bulkAdjustmentType === 'add'}
                                                onChange={(e) => setBulkAdjustmentType(e.target.value as 'add' | 'remove')}
                                                className="mr-2"
                                            />
                                            Add Stock (+)
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="bulkAdjustmentType"
                                                value="remove"
                                                checked={bulkAdjustmentType === 'remove'}
                                                onChange={(e) => setBulkAdjustmentType(e.target.value as 'add' | 'remove')}
                                                className="mr-2"
                                            />
                                            Remove Stock (-)
                                        </label>
                                    </div>
                                </div>

                                {/* Common adjustment tools */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            {bulkAdjustmentType === 'add' ? 'Add to All' : 'Remove from All'}
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            placeholder="Quantity"
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                const num = value ? parseInt(value) : 0;
                                                if (num > 0) {
                                                    setBulkStockAdjustments(prev =>
                                                        prev.map(item => ({
                                                            ...item,
                                                            quantity: item.quantity + (bulkAdjustmentType === 'add' ? num : -num)
                                                        }))
                                                    );
                                                }
                                            }}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Set All To</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            placeholder="Quantity"
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                const num = value ? parseInt(value) : 0;
                                                if (num >= 0) {
                                                    setBulkStockAdjustments(prev =>
                                                        prev.map(item => ({
                                                            ...item,
                                                            quantity: num
                                                        }))
                                                    );
                                                }
                                            }}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {/* Common fields for both modes */}
                        {/* Warehouse Selection */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Warehouse</label>
                            <select
                                value={selectedWarehouse ?? ''}
                                onChange={(e) => setSelectedWarehouse(e.target.value ? Number(e.target.value) : null)}
                                required
                                className="w-full px-3 py-2 border rounded-lg"
                            >
                                <option value="">Select warehouse</option>
                                {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Reason */}
                        {bulkStockMode ? (
                            <>
                                {/* Reason for bulk mode */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">Reason</label>
                                    <select
                                        value={bulkReason}
                                        onChange={(e) => setBulkReason(e.target.value as StockAdjustmentReason)}
                                        required
                                        className="w-full px-3 py-2 border rounded-lg"
                                    >
                                        {bulkCurrentReasons.map(r => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Custom Reason Text */}
                                {bulkReason === 'other' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Specify reason</label>
                                        <input
                                            type="text"
                                            value={bulkReasonText}
                                            onChange={(e) => setBulkReasonText(e.target.value)}
                                            required
                                            placeholder="Enter reason"
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium mb-1">Reason</label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value as StockAdjustmentReason)}
                                    required
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    {currentReasons.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Reference Number */}
                        {currentReasons.find(r => r.value === reason)?.requiresRef && (
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Reference Number <span className="text-gray-400">(PO, RMA, etc)</span>
                                </label>
                                <input
                                    type="text"
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    placeholder="e.g., PO-2024-001"
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                        )}

                        {/* Custom Reason Text */}
                        {reason === 'other' && (
                            <div>
                                <label className="block text-sm font-medium mb-1">Specify reason</label>
                                <input
                                    type="text"
                                    value={reasonText}
                                    onChange={(e) => setReasonText(e.target.value)}
                                    required
                                    placeholder="Enter reason"
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                placeholder="Additional details..."
                                className="w-full px-3 py-2 border rounded-lg"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || (bulkStockMode
                                    ? bulkStockAdjustments.filter(a => a.quantity > 0).length === 0
                                    : quantity <= 0)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Adjusting...' : bulkStockMode
                                    ? `Update ${bulkStockAdjustments.filter(a => a.quantity > 0).length} Variants`
                                    : 'Confirm Adjustment'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StockAdjustmentModal;